import type { Invoice, InvoiceAdd, InvoiceUpdate } from "../models/invoice";
import type {
	Workspace,
	WorkspaceAdd,
	WorkspaceAlert,
	WorkspaceGroup,
	WorkspaceGroupAdd,
	WorkspaceGroupUpdate,
	WorkspaceReminder,
	WorkspaceUpdate,
} from "../models/workspace";
import { createCrudStoreBundle } from "./crud.store";
import { type ApiClient, runStoreAction } from "./shared";

export type { Invoice } from "../models/invoice";
export type { WorkspaceAlert, WorkspaceGroup, WorkspaceReminder } from "../models/workspace";

export type WorkspaceEntity = Workspace;
export type WorkspaceAlertEntity = WorkspaceAlert & { readonly id: string };
export type WorkspaceReminderEntity = WorkspaceReminder & { readonly id: string };
export type WorkspaceGroupEntity = WorkspaceGroup;

/** Alert/reminder drafts may omit id; store assigns one before persist. */
export type WorkspaceAlertInput = Omit<WorkspaceAlert, "id"> & { readonly id?: string };
export type WorkspaceReminderInput = Omit<WorkspaceReminder, "id"> & { readonly id?: string };

export const createWorkspacesStore = (apiClient: ApiClient) => {
	const bundle = createCrudStoreBundle<WorkspaceEntity, WorkspaceAdd, WorkspaceUpdate>({
		apiClient,
		entityPath: "workspaces",
	});

	const updateWorkspace = async (workspace: WorkspaceUpdate): Promise<WorkspaceEntity> => {
		const updatedWorkspace = await apiClient.post<WorkspaceEntity>(`/update/workspaces/${workspace.id}`, workspace);
		bundle.useStore.getState().upsertOne(updatedWorkspace);
		return updatedWorkspace;
	};

	const uploadLogo = async (workspaceId: string, file: File): Promise<WorkspaceEntity> => {
		const updatedWorkspace = await apiClient.post<WorkspaceEntity>(`/workspaces/${workspaceId}/logo`, file, {
			"Content-Type": file.type,
		});
		bundle.useStore.getState().upsertOne(updatedWorkspace);
		return updatedWorkspace;
	};

	const deleteLogo = async (workspaceId: string): Promise<WorkspaceEntity> => {
		const updatedWorkspace = await apiClient.delete<WorkspaceEntity>(`/workspaces/${workspaceId}/logo`);
		bundle.useStore.getState().upsertOne(updatedWorkspace);
		return updatedWorkspace;
	};

	const addGroup = async (group: WorkspaceGroupAdd): Promise<WorkspaceGroupEntity> => {
		const state = bundle.useStore.getState();
		return runStoreAction(state.setLoading, state.setError, async () =>
			apiClient.post<WorkspaceGroupEntity>("/add/groups", group)
		);
	};

	const updateGroup = async (group: WorkspaceGroupUpdate): Promise<WorkspaceGroupEntity> => {
		const state = bundle.useStore.getState();
		return runStoreAction(state.setLoading, state.setError, async () =>
			apiClient.post<WorkspaceGroupEntity>(`/update/groups/${group.id}`, group)
		);
	};

	const deleteGroup = async (groupId: string): Promise<void> => {
		const state = bundle.useStore.getState();
		await runStoreAction(state.setLoading, state.setError, async () => {
			await apiClient.delete<unknown>(`/delete/groups/${groupId}`);
		});
	};

	const addAlerts = async (workspaceId: string, alerts: readonly WorkspaceAlertInput[]): Promise<WorkspaceEntity> => {
		const state = bundle.useStore.getState();
		return runStoreAction(state.setLoading, state.setError, async () => {
			const workspace = state.entities[workspaceId];
			if (!workspace) {
				throw new Error("workspace.not-exist");
			}
			const withIds = alerts.map(
				(alert): WorkspaceAlertEntity => ({
					...alert,
					id: typeof alert.id === "string" && alert.id.length > 0 ? alert.id : crypto.randomUUID(),
					name: alert.name,
					alertType: alert.alertType,
					receiver: alert.receiver,
				})
			);
			return updateWorkspace({
				...workspace,
				alerts: [...(workspace.alerts ?? []), ...withIds],
			});
		});
	};

	const updateAlert = async (workspaceId: string, alert: WorkspaceAlertEntity): Promise<WorkspaceEntity> => {
		const state = bundle.useStore.getState();
		return runStoreAction(state.setLoading, state.setError, async () => {
			const workspace = state.entities[workspaceId];
			if (!workspace) {
				throw new Error("workspace.not-exist");
			}
			return updateWorkspace({
				...workspace,
				alerts: (workspace.alerts ?? []).map((item) => (item.id === alert.id ? alert : item)),
			});
		});
	};

	const deleteAlert = async (workspaceId: string, alertId: string): Promise<WorkspaceEntity> => {
		const state = bundle.useStore.getState();
		return runStoreAction(state.setLoading, state.setError, async () => {
			const workspace = state.entities[workspaceId];
			if (!workspace) {
				throw new Error("workspace.not-exist");
			}
			return updateWorkspace({
				...workspace,
				alerts: (workspace.alerts ?? []).filter((item) => item.id !== alertId),
			});
		});
	};

	const addReminders = async (
		workspaceId: string,
		reminders: readonly WorkspaceReminderInput[]
	): Promise<WorkspaceEntity> => {
		const state = bundle.useStore.getState();
		return runStoreAction(state.setLoading, state.setError, async () => {
			const workspace = state.entities[workspaceId];
			if (!workspace) {
				throw new Error("workspace.not-exist");
			}
			const withIds = reminders.map(
				(reminder): WorkspaceReminderEntity => ({
					...reminder,
					id: typeof reminder.id === "string" && reminder.id.length > 0 ? reminder.id : crypto.randomUUID(),
					trackedType: reminder.trackedType,
					sendReminderAtHourUTC: reminder.sendReminderAtHourUTC,
					sendOnDays: reminder.sendOnDays,
				})
			);
			return updateWorkspace({
				...workspace,
				reminders: [...(workspace.reminders ?? []), ...withIds],
			});
		});
	};

	const updateReminder = async (workspaceId: string, reminder: WorkspaceReminderEntity): Promise<WorkspaceEntity> => {
		const state = bundle.useStore.getState();
		return runStoreAction(state.setLoading, state.setError, async () => {
			const workspace = state.entities[workspaceId];
			if (!workspace) {
				throw new Error("workspace.not-exist");
			}
			return updateWorkspace({
				...workspace,
				reminders: (workspace.reminders ?? []).map((item) => (item.id === reminder.id ? reminder : item)),
			});
		});
	};

	const deleteReminder = async (workspaceId: string, reminderId: string): Promise<WorkspaceEntity> => {
		const state = bundle.useStore.getState();
		return runStoreAction(state.setLoading, state.setError, async () => {
			const workspace = state.entities[workspaceId];
			if (!workspace) {
				throw new Error("workspace.not-exist");
			}
			return updateWorkspace({
				...workspace,
				reminders: (workspace.reminders ?? []).filter((item) => item.id !== reminderId),
			});
		});
	};

	const addInvoice = async (invoice: InvoiceAdd): Promise<readonly Invoice[]> => {
		const state = bundle.useStore.getState();
		return runStoreAction(state.setLoading, state.setError, async () =>
			apiClient.post<readonly Invoice[]>("/add/invoices", invoice)
		);
	};

	const updateInvoice = async (invoice: InvoiceUpdate): Promise<readonly Invoice[]> => {
		const state = bundle.useStore.getState();
		return runStoreAction(state.setLoading, state.setError, async () =>
			apiClient.post<readonly Invoice[]>(`/update/invoices/${invoice.id}`, invoice)
		);
	};

	const deleteInvoice = async (invoiceId: string): Promise<void> => {
		const state = bundle.useStore.getState();
		await runStoreAction(state.setLoading, state.setError, async () => {
			await apiClient.delete<unknown>(`/delete/invoices/${invoiceId}`);
		});
	};

	const getInvoices = async (): Promise<readonly Invoice[]> => {
		const state = bundle.useStore.getState();
		return runStoreAction(state.setLoading, state.setError, async () =>
			apiClient.get<readonly Invoice[]>("/get/invoices")
		);
	};

	return {
		useStore: bundle.useStore,
		actions: {
			...bundle.actions,
			addGroup,
			updateGroup,
			deleteGroup,
			addAlerts,
			updateAlert,
			deleteAlert,
			addReminders,
			updateReminder,
			deleteReminder,
			addInvoice,
			updateInvoice,
			deleteInvoice,
			getInvoices,
			uploadLogo,
			deleteLogo,
		},
	};
};
